import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { AppConfig, Config, ConfigService, IAppConfig, IConfig, IConfigService, UpdateGeneralConfigOptions } from '@posx/core';
import { Subscription } from 'dexie';
import { getPlatform } from '@/utils/misc';
import { setCurrencySymbol, setSymbolAfterAmount } from '@/utils/formatNumber';
import { logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';


export const useConfigStore = defineStore('config', {
  state: () => ({
    service: null as IConfigService | null,
    appConfig: null as IAppConfig | null,
    _subscription: null as Subscription | null,
  }),
  actions: {
    async getService() : Promise<IConfigService>  {
      if (this.service) return this.service;
      else {
        // service singleton is method-only, skip deep proxy
        this.service = markRaw(await ServicesBuilder.getInstance().getService<IConfigService>(ConfigService));
        return this.service;
      }
    },
    async startLiveQuery(): Promise<void> {
      if (getPlatform() === 'mobile-web') return;
      if (this._subscription) return;
      const service = await this.getService();
      const sub: Subscription = service.liveQuery(() => service.getDefaultOne())
        .subscribe({
          next: (result: IAppConfig) => {
            this.appConfig = result || new AppConfig();
            setCurrencySymbol(result?.config?.currency?.currency_symbol);
            setSymbolAfterAmount(result?.config?.currency?.is_symbol_after_amount);
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'config' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } }
        });
      this._subscription = markRaw(sub);
    },
    stopLiveQuery() {
      if (this._subscription) {
        this._subscription.unsubscribe();
        this._subscription = null;
      }
    },
    async updateGeneralConfig(payload: UpdateGeneralConfigOptions) {
      const service = await this.getService();
      return await service?.updateGeneralConfig(payload);
    },
    async updateConfig(key: keyof IConfig, payload: Partial<IConfig[typeof key]>) {
      const service = await this.getService();
      return service?.updateConfig(key, payload);
    },
    async updateStorage(key: 'pricing_lookup_keys' | 'pay_in_reasons' | 'pay_out_reasons', value: any) {
      const service = await this.getService();
      console.log('updateStorage', key, value);
      // Update current storage and save to cloud
      if (this.appConfig && this.appConfig.id_in_server) {
        // Ensure storage object exists //unknown
        if (!this.appConfig.storage) {
          this.appConfig.storage = {} as any;
        }
        // Update specified field
        this.appConfig.storage[key] = value;
        // Save entire appConfig through service
        await service.updateOne(this.appConfig.id_in_server, this.appConfig);
      }
    },
  },
});
